<script lang="ts" setup>
/**
 * Safari 浏览器 mockup — Inspira UI
 * 文档：https://inspira-ui.com/docs/en/components/device-mocks/safari-mockup
 * 视频用法对齐 Magic UI：传 videoSrc，或 src 为视频扩展名
 */
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import TypewriterText from '@/components/common/TypewriterText.vue';

const VIDEO_EXT_RE = /\.(mp4|webm|ogg|mov)(\?.*)?$/i;
const TOOLBAR_H = 52;
const DEFAULT_WIDTH = 1203;
const DEFAULT_HEIGHT = 753;
const DEFAULT_CONTENT_H = 700;

interface SafariMockupProps {
  url?: string;
  /** 内容区图片；若为视频扩展名且未传 videoSrc，则按视频播放 */
  src?: string;
  /** 内容区视频（优先于 src） */
  videoSrc?: string;
  /** 窗体宽度（含边框），默认 1203 */
  width?: number;
  /**
   * 窗体总高度。若同时传 contentAspectRatio，以比例推算为准。
   * 默认 753（内容区约 1200×700）
   */
  height?: number;
  /**
   * 内容区宽高比（宽/高），如 16/9。
   * 传入后按 width 推算内容高度与整窗高度，真正改变窗体比例。
   */
  contentAspectRatio?: number;
  /** 窗口圆角，默认 20 */
  radius?: number;
  /** 地址栏打字机动画 */
  typewriter?: boolean;
  /** 视频是否静音，默认 true（便于自动播放） */
  muted?: boolean;
  /** 视频是否循环，默认 true */
  loop?: boolean;
  /** 视频是否自动播放，默认 true */
  autoplay?: boolean;
  /** 是否处于可播放状态（视口外 / 切标签页时传 false 以暂停） */
  playing?: boolean;
  /** 媒体适配：cover 铺满裁切；contain 完整显示
   */
  objectFit?: 'cover' | 'contain';
  /** video 预加载策略，切换多源时建议 auto */
  videoPreload?: 'none' | 'metadata' | 'auto';
  /** 多视频池：每源独立 video，切换时旧画面保持至新源就绪 */
  videoSources?: string[];
}

const props = withDefaults(defineProps<SafariMockupProps>(), {
  width: DEFAULT_WIDTH,
  height: DEFAULT_HEIGHT,
  radius: 20,
  typewriter: true,
  muted: true,
  loop: true,
  autoplay: true,
  playing: true,
  objectFit: 'cover',
  videoPreload: 'metadata',
});

const uid = useId();
const clipOuterId = `safari-outer-${uid}`;
const clipContentId = `safari-content-${uid}`;
const videoRef = ref<HTMLVideoElement | null>(null);
const videoRefs = new Map<string, HTMLVideoElement>();
/** 池模式下实际显示的视频源，滞后于 resolvedVideoSrc 以避免黑屏 */
const visibleSrc = ref('');

const resolvedVideoSrc = computed(() => {
  if (props.videoSrc) return props.videoSrc;
  if (props.src && VIDEO_EXT_RE.test(props.src)) return props.src;
  return '';
});

const useVideoPool = computed(
  () => Boolean(props.videoSources?.length && props.videoSources.length > 1),
);

const poolSources = computed(() => {
  if (useVideoPool.value) return props.videoSources!;
  if (resolvedVideoSrc.value) return [resolvedVideoSrc.value];
  return [];
});

const isVideo = computed(() => Boolean(resolvedVideoSrc.value));
const imageSrc = computed(() => (isVideo.value ? '' : props.src || ''));
const imagePreserveAspectRatio = computed(() =>
  props.objectFit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice',
);

/** 内容区尺寸：可由 contentAspectRatio 驱动 */
const contentWidth = computed(() => Math.max(props.width - 3, 1));
const contentHeight = computed(() => {
  if (props.contentAspectRatio && props.contentAspectRatio > 0) {
    return Math.round(contentWidth.value / props.contentAspectRatio);
  }
  // 未指定比例时：随 height 伸缩，保持原版 753→700 的关系
  if (props.height !== DEFAULT_HEIGHT) {
    return Math.max(props.height - TOOLBAR_H - 1, 1);
  }
  return DEFAULT_CONTENT_H;
});

const frameWidth = computed(() => Math.max(props.width - 1, 1));
const frameHeight = computed(() => TOOLBAR_H + contentHeight.value);
const svgWidth = computed(() => props.width);
const svgHeight = computed(() => frameHeight.value);

/** 视频 HTML 层定位（与 SVG 内容区几何对齐，避开 foreignObject） */
const mediaOverlayStyle = computed(() => {
  const sw = svgWidth.value;
  const sh = svgHeight.value;
  const cw = contentWidth.value;
  const ch = contentHeight.value;
  const radiusX = (props.radius / cw) * 100;
  const radiusY = (props.radius / ch) * 100;

  return {
    left: `${(1 / sw) * 100}%`,
    top: `${(TOOLBAR_H / sh) * 100}%`,
    width: `${(cw / sw) * 100}%`,
    height: `${(ch / sh) * 100}%`,
    borderBottomLeftRadius: `${radiusX}% ${radiusY}%`,
    borderBottomRightRadius: `${radiusX}% ${radiusY}%`,
  };
});

const videoObjectFitClass = computed(() =>
  props.objectFit === 'contain' ? 'safari-media__video--contain' : 'safari-media__video--cover',
);

function waitForVideoFrame(el: HTMLVideoElement) {
  if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const onReady = () => {
      el.removeEventListener('loadeddata', onReady);
      el.removeEventListener('canplay', onReady);
      resolve();
    };
    el.addEventListener('loadeddata', onReady, { once: true });
    el.addEventListener('canplay', onReady, { once: true });
  });
}

function setPoolVideoRef(src: string) {
  return (el: HTMLVideoElement | null) => {
    if (el) videoRefs.set(src, el);
    else videoRefs.delete(src);
  };
}

async function syncPlayback() {
  if (useVideoPool.value) {
    videoRefs.forEach((el, src) => {
      const isActive = src === visibleSrc.value;
      if (isActive && props.playing && props.autoplay) {
        el.play().catch(() => {});
        return;
      }
      el.pause();
    });
    return;
  }

  const el = videoRef.value;
  if (!el || !isVideo.value) return;
  if (props.playing && props.autoplay) {
    try {
      await el.play();
    } catch {
      /* 浏览器策略拦截时忽略 */
    }
    return;
  }
  el.pause();
}

async function activateVideoSrc(targetSrc: string) {
  if (!targetSrc) return;

  if (!useVideoPool.value) {
    visibleSrc.value = targetSrc;
    await nextTick();
    const el = videoRef.value;
    if (el && isVideo.value) await waitForVideoFrame(el);
    await syncPlayback();
    return;
  }

  await nextTick();
  const el = videoRefs.get(targetSrc);

  // 首次加载：直接显示目标源，避免池内全部 opacity 0 黑屏
  if (!visibleSrc.value) {
    visibleSrc.value = targetSrc;
    if (el) await waitForVideoFrame(el);
    await syncPlayback();
    return;
  }

  if (el) await waitForVideoFrame(el);
  visibleSrc.value = targetSrc;
  await syncPlayback();
}

watch(
  () => resolvedVideoSrc.value,
  (targetSrc) => {
    void activateVideoSrc(targetSrc);
  },
  { immediate: true },
);

watch(
  () => [props.playing, props.autoplay, isVideo.value] as const,
  async () => {
    await syncPlayback();
  },
);

onBeforeUnmount(() => {
  videoRef.value?.pause();
  videoRefs.forEach((el) => el.pause());
});

// 圆角贝塞尔近似系数（与原 SVG 12px 圆角比例一致）
const bez = computed(() => props.radius * 0.447715);
const bezBottom = computed(() => props.radius * 0.552284);

const innerRadius = computed(() => Math.max(props.radius - 1, 1));
const innerBez = computed(() => innerRadius.value * 0.447715);

const frameBottomPath = computed(
  () =>
    `M0 ${TOOLBAR_H}H${frameWidth.value}V${frameHeight.value - props.radius}C${frameWidth.value} ${frameHeight.value - props.radius + bezBottom.value} ${frameWidth.value - bezBottom.value} ${frameHeight.value} ${frameWidth.value - props.radius} ${frameHeight.value}H${props.radius}C${bez.value} ${frameHeight.value} 0 ${frameHeight.value - props.radius + bezBottom.value} 0 ${frameHeight.value - props.radius}V${TOOLBAR_H}Z`,
);

const frameTopPath = computed(
  () =>
    `M0 ${props.radius}C0 ${bez.value} ${bez.value} 0 ${props.radius} 0H${frameWidth.value - props.radius}C${frameWidth.value - props.radius + bez.value} 0 ${frameWidth.value} ${bez.value} ${frameWidth.value} ${props.radius}V${TOOLBAR_H}H0Z`,
);

const frameTopInnerPath = computed(
  () =>
    `M1.06738 ${innerRadius.value}C1.06738 ${innerBez.value + 1} ${innerBez.value + 1} 1 ${innerRadius.value + 1.067} 1H${frameWidth.value - innerRadius.value - 1.067}C${frameWidth.value - innerRadius.value} 1 ${frameWidth.value - 1.067} ${innerBez.value + 1} ${frameWidth.value - 1.067} ${innerRadius.value}V${TOOLBAR_H - 1}H1.06738V${innerRadius.value}Z`,
);

const contentClipPath = computed(
  () =>
    `M1 ${TOOLBAR_H}H${frameWidth.value - 1}V${frameHeight.value - props.radius - 1}C${frameWidth.value - 1} ${frameHeight.value - props.radius - 1 + bezBottom.value} ${frameWidth.value - bezBottom.value} ${frameHeight.value - 1} ${frameWidth.value - props.radius} ${frameHeight.value - 1}H${props.radius + 1}C${bez.value + 1} ${frameHeight.value - 1} 1 ${frameHeight.value - props.radius - 1 + bezBottom.value} 1 ${frameHeight.value - props.radius - 1}V${TOOLBAR_H}Z`,
);
</script>

<template>
  <!-- aspect-ratio 预留占位，避免 Safari 缩放 SVG 时 foreignObject 溢出 -->
  <div
    class="safari-mockup-root"
    :style="{ aspectRatio: `${svgWidth} / ${svgHeight}` }"
  >
    <!-- Safari 对 foreignObject 内 video 的 object-fit 不可靠，改用 HTML 叠层 -->
    <div
      v-if="isVideo"
      class="safari-media-overlay"
      :style="mediaOverlayStyle"
    >
      <div
        class="safari-media"
        :class="{ 'safari-media--pool': useVideoPool }"
      >
        <template v-if="useVideoPool">
          <video
            v-for="src in poolSources"
            :key="src"
            :ref="setPoolVideoRef(src)"
            class="safari-media__video"
            :class="[
              videoObjectFitClass,
              { 'safari-media__video--active': src === visibleSrc },
            ]"
            :src="src"
            :muted="muted"
            :loop="loop"
            playsinline
            :preload="videoPreload"
          />
        </template>
        <video
          v-else
          ref="videoRef"
          class="safari-media__video"
          :class="videoObjectFitClass"
          :src="resolvedVideoSrc"
          :muted="muted"
          :loop="loop"
          :autoplay="autoplay && playing"
          playsinline
          :preload="videoPreload"
          @loadeddata="syncPlayback"
        />
      </div>
    </div>

  <svg
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
    preserveAspectRatio="xMidYMid meet"
    width="100%"
    height="100%"
    class="safari-mockup-svg"
  >
    <g :clip-path="`url(#${clipOuterId})`">
      <path
        :d="frameBottomPath"
        class="fill-[#E5E5E5] dark:fill-[#404040]"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        :d="frameTopPath"
        class="fill-[#E5E5E5] dark:fill-[#404040]"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        :d="frameTopInnerPath"
        class="fill-white dark:fill-[#262626]"
      />
      <circle
        cx="27"
        cy="25"
        r="6"
        class="fill-[#E5E5E5] dark:fill-[#404040]"
      />
      <circle
        cx="47"
        cy="25"
        r="6"
        class="fill-[#E5E5E5] dark:fill-[#404040]"
      />
      <circle
        cx="67"
        cy="25"
        r="6"
        class="fill-[#E5E5E5] dark:fill-[#404040]"
      />
      <path
        d="M286 17C286 13.6863 288.686 11 292 11H946C949.314 11 952 13.6863 952 17V35C952 38.3137 949.314 41 946 41H292C288.686 41 286 38.3137 286 35V17Z"
        class="fill-[#E5E5E5] dark:fill-[#404040]"
      />
      <!-- 描述文案模式不显示锁图标，避免遮挡文字 -->
      <g class="mix-blend-luminosity">
        <foreignObject
          x="292"
          y="11"
          width="654"
          height="30"
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            class="safari-address-bar-text"
          >
            <TypewriterText
              v-if="typewriter && url"
              :key="url"
              :text="url"
              :speed="38"
            />
            <template v-else>{{ url }}</template>
          </div>
        </foreignObject>
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M265.5 33.8984C265.641 33.8984 265.852 33.8516 266.047 33.7422C270.547 31.2969 272.109 30.1641 272.109 27.3203V21.4219C272.109 20.4844 271.742 20.1484 270.961 19.8125C270.094 19.4453 267.18 18.4297 266.328 18.1406C266.07 18.0547 265.766 18 265.5 18C265.234 18 264.93 18.0703 264.672 18.1406C263.82 18.3828 260.906 19.4531 260.039 19.8125C259.258 20.1406 258.891 20.4844 258.891 21.4219V27.3203C258.891 30.1641 260.461 31.2812 264.945 33.7422C265.148 33.8516 265.359 33.8984 265.5 33.8984ZM265.922 19.5781C266.945 19.9766 269.172 20.7656 270.344 21.1875C270.562 21.2656 270.617 21.3828 270.617 21.6641V27.0234C270.617 29.3125 269.469 29.9375 265.945 32.0625C265.727 32.1875 265.617 32.2344 265.508 32.2344V19.4844C265.617 19.4844 265.734 19.5156 265.922 19.5781Z"
          fill="#A3A3A3"
        />
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M936.273 24.9766C936.5 24.9766 936.68 24.9062 936.82 24.7578L940.023 21.5312C940.195 21.3594 940.273 21.1719 940.273 20.9531C940.273 20.7422 940.188 20.5391 940.023 20.3828L936.82 17.125C936.68 16.9688 936.5 16.8906 936.273 16.8906C935.852 16.8906 935.516 17.2422 935.516 17.6719C935.516 17.8828 935.594 18.0547 935.727 18.2031L937.594 20.0312C937.227 19.9766 936.852 19.9453 936.477 19.9453C932.609 19.9453 929.516 23.0391 929.516 26.9141C929.516 30.7891 932.633 33.9062 936.5 33.9062C940.375 33.9062 943.484 30.7891 943.484 26.9141C943.484 26.4453 943.156 26.1094 942.688 26.1094C942.234 26.1094 941.93 26.4453 941.93 26.9141C941.93 29.9297 939.516 32.3516 936.5 32.3516C933.492 32.3516 931.07 29.9297 931.07 26.9141C931.07 23.875 933.469 21.4688 936.477 21.4688C936.984 21.4688 937.453 21.5078 937.867 21.5781L935.734 23.6875C935.594 23.8281 935.516 24 935.516 24.2109C935.516 24.6406 935.852 24.9766 936.273 24.9766Z"
          fill="#A3A3A3"
        />
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M1134 33.0156C1134.49 33.0156 1134.89 32.6094 1134.89 32.1484V27.2578H1139.66C1140.13 27.2578 1140.54 26.8594 1140.54 26.3672C1140.54 25.8828 1140.13 25.4766 1139.66 25.4766H1134.89V20.5859C1134.89 20.1172 1134.49 19.7188 1134 19.7188C1133.52 19.7188 1133.11 20.1172 1133.11 20.5859V25.4766H1128.34C1127.88 25.4766 1127.46 25.8828 1127.46 26.3672C1127.46 26.8594 1127.88 27.2578 1128.34 27.2578H1133.11V32.1484C1133.11 32.6094 1133.52 33.0156 1134 33.0156Z"
          fill="#A3A3A3"
        />
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M1161.8 31.0703H1163.23V32.375C1163.23 34.0547 1164.12 34.9219 1165.81 34.9219H1174.2C1175.89 34.9219 1176.77 34.0547 1176.77 32.3828V24.0469C1176.77 22.375 1175.89 21.5 1174.2 21.5H1172.77V20.2578C1172.77 18.5859 1171.88 17.7109 1170.19 17.7109H1161.8C1160.1 17.7109 1159.23 18.5781 1159.23 20.2578V28.5234C1159.23 30.1953 1160.1 31.0703 1161.8 31.0703ZM1161.9 29.5078C1161.18 29.5078 1160.78 29.1328 1160.78 28.3828V20.3984C1160.78 19.6406 1161.18 19.2656 1161.9 19.2656H1170.09C1170.8 19.2656 1171.2 19.6406 1171.2 20.3984V21.5H1165.81C1164.12 21.5 1163.23 22.375 1163.23 24.0469V29.5078H1161.9ZM1165.91 33.3672C1165.19 33.3672 1164.8 32.9922 1164.8 32.2422V24.1875C1164.8 23.4297 1165.19 23.0625 1165.91 23.0625H1174.1C1174.81 23.0625 1175.21 23.4297 1175.21 24.1875V32.2422C1175.21 32.9922 1174.81 33.3672 1174.1 33.3672H1165.91Z"
          fill="#A3A3A3"
        />
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M1099.51 28.4141C1099.91 28.4141 1100.24 28.0859 1100.24 27.6953V19.8359L1100.18 18.6797L1100.66 19.25L1101.75 20.4141C1101.88 20.5547 1102.06 20.625 1102.24 20.625C1102.6 20.625 1102.9 20.3672 1102.9 20C1102.9 19.8047 1102.82 19.6641 1102.69 19.5312L1100.06 17.0078C1099.88 16.8203 1099.7 16.7578 1099.51 16.7578C1099.32 16.7578 1099.14 16.8203 1098.95 17.0078L1096.33 19.5312C1096.2 19.6641 1096.12 19.8047 1096.12 20C1096.12 20.3672 1096.41 20.625 1096.77 20.625C1096.95 20.625 1097.14 20.5547 1097.27 20.4141L1098.35 19.25L1098.84 18.6719L1098.78 19.8359V27.6953C1098.78 28.0859 1099.11 28.4141 1099.51 28.4141ZM1095 34.6562H1104C1105.7 34.6562 1106.57 33.7812 1106.57 32.1094V24.4297C1106.57 22.7578 1105.7 21.8828 1104 21.8828H1101.89V23.4375H1103.9C1104.61 23.4375 1105.02 23.8125 1105.02 24.5625V31.9688C1105.02 32.7188 1104.61 33.0938 1103.9 33.0938H1095.1C1094.38 33.0938 1093.98 32.7188 1093.98 31.9688V24.5625C1093.98 23.8125 1094.38 23.4375 1095.1 23.4375H1097.13V21.8828H1095C1093.31 21.8828 1092.43 22.75 1092.43 24.4297V32.1094C1092.43 33.7812 1093.31 34.6562 1095 34.6562Z"
          fill="#A3A3A3"
        />
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M99.5703 33.6016H112.938C114.633 33.6016 115.516 32.7266 115.516 31.0547V21.5469C115.516 19.875 114.633 19 112.938 19H99.5703C97.8828 19 97 19.8672 97 21.5469V31.0547C97 32.7266 97.8828 33.6016 99.5703 33.6016ZM99.6719 32.0469C98.9531 32.0469 98.5547 31.6719 98.5547 30.9141V21.6875C98.5547 20.9297 98.9531 20.5547 99.6719 20.5547H103.234V32.0469H99.6719ZM112.836 20.5547C113.555 20.5547 113.953 20.9297 113.953 21.6875V30.9141C113.953 31.6719 113.555 32.0469 112.836 32.0469H104.711V20.5547H112.836ZM101.703 23.4141C101.984 23.4141 102.219 23.1719 102.219 22.9062C102.219 22.6406 101.984 22.4062 101.703 22.4062H100.102C99.8203 22.4062 99.5859 22.6406 99.5859 22.9062C99.5859 23.1719 99.8203 23.4141 100.102 23.4141H101.703ZM101.703 25.5156C101.984 25.5156 102.219 25.2812 102.219 25.0078C102.219 24.7422 101.984 24.5078 101.703 24.5078H100.102C99.8203 24.5078 99.5859 24.7422 99.5859 25.0078C99.5859 25.2812 99.8203 25.5156 100.102 25.5156H101.703ZM101.703 27.6094C101.984 27.6094 102.219 27.3828 102.219 27.1094C102.219 26.8438 101.984 26.6172 101.703 26.6172H100.102C99.8203 26.6172 99.5859 26.8438 99.5859 27.1094C99.5859 27.3828 99.8203 27.6094 100.102 27.6094H101.703Z"
          fill="#A3A3A3"
        />
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M143.914 32.5938C144.094 32.7656 144.312 32.8594 144.562 32.8594C145.086 32.8594 145.492 32.4531 145.492 31.9375C145.492 31.6797 145.391 31.4453 145.211 31.2656L139.742 25.9219L145.211 20.5938C145.391 20.4141 145.492 20.1719 145.492 19.9219C145.492 19.4062 145.086 19 144.562 19C144.312 19 144.094 19.0938 143.922 19.2656L137.844 25.2031C137.625 25.4062 137.516 25.6562 137.516 25.9297C137.516 26.2031 137.625 26.4375 137.836 26.6484L143.914 32.5938Z"
          fill="#A3A3A3"
        />
      </g>
      <g class="mix-blend-luminosity">
        <path
          d="M168.422 32.8594C168.68 32.8594 168.891 32.7656 169.07 32.5938L175.148 26.6562C175.359 26.4375 175.469 26.2109 175.469 25.9297C175.469 25.6562 175.367 25.4141 175.148 25.2109L169.07 19.2656C168.891 19.0938 168.68 19 168.422 19C167.898 19 167.492 19.4062 167.492 19.9219C167.492 20.1719 167.602 20.4141 167.773 20.5938L173.25 25.9375L167.773 31.2656C167.594 31.4531 167.492 31.6797 167.492 31.9375C167.492 32.4531 167.898 32.8594 168.422 32.8594Z"
          fill="#A3A3A3"
        />
      </g>
      <g v-if="imageSrc" :clip-path="`url(#${clipContentId})`">
      <image
        :width="contentWidth"
        :height="contentHeight"
        x="1"
        :y="TOOLBAR_H"
        :preserveAspectRatio="imagePreserveAspectRatio"
        :href="imageSrc"
      />
      </g>
    </g>
    <defs>
      <clipPath :id="clipOuterId">
        <rect
          fill="white"
          :width="svgWidth"
          :height="svgHeight"
        />
      </clipPath>
      <clipPath :id="clipContentId">
        <path
          :d="contentClipPath"
          fill="white"
        />
      </clipPath>
    </defs>
  </svg>
  </div>
</template>

<style scoped>
.safari-mockup-root {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.safari-media-overlay {
  position: absolute;
  z-index: 2;
  overflow: hidden;
  background: #0a0a0a;
}

.safari-mockup-svg {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
}

.safari-address-bar-text {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 100%;
  padding: 0 36px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 11px;
  line-height: 1;
  color: #737373;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
}

.safari-address-bar-text :deep(.typewriter-text) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.safari-media {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.safari-media--pool .safari-media__video {
  opacity: 0;
  z-index: 0;
}

.safari-media--pool .safari-media__video--active {
  opacity: 1;
  z-index: 1;
}

.safari-media__video {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
  object-position: center;
}

.safari-media__video--cover {
  object-fit: cover;
}

.safari-media__video--contain {
  object-fit: contain;
}
</style>
